import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { DashboardSubmissionSummary } from '@/core/models/workspace.models';
import { workspaceService } from '@/core/services/workspace';
import { accountsLink } from './accountsFilters';
import './SubmissionCharts.scss';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type SliceKey = 'overdue' | 'due' | 'submitted' | 'approved' | 'sentForSignature' | 'notAvailable';

interface Slice {
  key: SliceKey;
  label: string;
  color: string;
  /** Drawn in the donut and counted in its total. Approved / sent for signature are extra figures, not slices. */
  inDonut: boolean;
  /** The `status` the Accounts list is opened with; none for a figure the list cannot filter on. */
  status?: 'overdue' | 'due' | 'submitted' | 'approved';
  /** Only shown when the response carries the figure. */
  optional?: boolean;
}

const SLICES: Slice[] = [
  { key: 'overdue', label: 'Overdue', color: '#e53935', inDonut: true, status: 'overdue' },
  { key: 'due', label: 'Due', color: '#f9a825', inDonut: true, status: 'due' },
  { key: 'submitted', label: 'Submitted', color: '#43a047', inDonut: true, status: 'submitted' },
  { key: 'approved', label: 'Approved', color: '#1e88e5', inDonut: false, status: 'approved', optional: true },
  { key: 'sentForSignature', label: 'Sent for signature', color: '#7b3f98', inDonut: false, optional: true },
  { key: 'notAvailable', label: 'Data not available', color: '#bdbdbd', inDonut: true },
];

const RADIUS = 80;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** A "nice" top for the y axis: at least 1, then a multiple that gives five even gridlines. */
export function axisMax(values: number[]): number {
  const max = Math.max(1, ...values);
  const step = Math.max(1, Math.ceil(max / 5));
  return step * 5;
}

/** BUG-055: the firm's submission summary (Workspace/getSubmissionSummary), loaded once and reloaded for another year. */
export function useSubmissionSummary() {
  const [summary, setSummary] = useState<DashboardSubmissionSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);

  const load = useCallback((year?: number) => {
    workspaceService.getSubmissionSummary(year).then(
      (loaded) => {
        if (!mounted.current) return;
        setSummary(loaded);
        setFailed(false);
      },
      () => {
        if (mounted.current) setFailed(true);
      },
    );
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  return { summary, failed, load };
}

interface WidgetProps {
  summary: DashboardSubmissionSummary | null;
  failed: boolean;
}

/** The figure for a slice, or null when it is an optional figure the response does not carry. */
function figure(summary: DashboardSubmissionSummary | null, slice: Slice): number | null {
  if (!summary) return slice.optional ? null : 0;
  const value = summary[slice.key];
  return typeof value === 'number' ? value : slice.optional ? null : 0;
}

/**
 * APT377: the compact, interactive "Accounts status" widget. Overdue, Due and Submitted (and Approved and Sent for
 * signature when the API returns them) each open the Accounts list with that status filter.
 */
export function AccountsStatusWidget({ summary, failed }: WidgetProps) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<SliceKey | null>(null);

  const rows = SLICES.map((slice) => ({ slice, value: figure(summary, slice) })).filter(
    (row): row is { slice: Slice; value: number } => row.value !== null,
  );
  const donutRows = rows.filter((row) => row.slice.inDonut);
  const total = donutRows.reduce((sum, row) => sum + row.value, 0);
  const centre = rows.find((row) => row.slice.key === hovered);

  let offset = 0;
  const arcs = summary
    ? donutRows.map((row) => {
        const length = total === 0 ? 0 : (row.value / total) * CIRCUMFERENCE;
        const arc = { ...row, length, offset };
        offset += length;
        return arc;
      })
    : [];

  return (
    <div className="dashboard-panel submission-charts submission-status">
      <div className="dashboard-panel-header">
        <h2 className="m-0">Accounts status</h2>
      </div>
      <div className="submission-charts-body">
        {failed && <div className="text-align-center p-1">The submission summary could not be loaded.</div>}
        <div className="submission-donut-wrap">
          <svg viewBox="0 0 200 200" className="submission-donut" role="img" aria-label="Accounts status">
            <circle cx="100" cy="100" r={RADIUS} fill="none" stroke="#eeeeee" strokeWidth="26" />
            {arcs
              .filter((arc) => arc.length > 0)
              .map((arc) => (
                <circle
                  key={arc.slice.key}
                  data-testid={`slice-${arc.slice.key}`}
                  cx="100"
                  cy="100"
                  r={RADIUS}
                  fill="none"
                  stroke={arc.slice.color}
                  strokeWidth={hovered === arc.slice.key ? 30 : 26}
                  strokeDasharray={`${arc.length} ${CIRCUMFERENCE - arc.length}`}
                  strokeDashoffset={-arc.offset}
                  transform="rotate(-90 100 100)"
                  className={arc.slice.status ? 'submission-arc-link' : undefined}
                  onMouseEnter={() => setHovered(arc.slice.key)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={
                    arc.slice.status ? () => void navigate(accountsLink({ status: arc.slice.status })) : undefined
                  }
                />
              ))}
            <text x="100" y="98" textAnchor="middle" className="submission-donut-title">
              {centre ? centre.slice.label : 'Total'}
            </text>
            <text x="100" y="120" textAnchor="middle" className="submission-donut-value">
              {summary ? (centre ? centre.value : total) : ''}
            </text>
          </svg>
          <ul className="submission-legend">
            {rows.map(({ slice, value }) => {
              const content = (
                <>
                  <span className="submission-dot" style={{ background: slice.color }} />
                  <span>{slice.label}</span>
                  <strong>{value}</strong>
                </>
              );
              return (
                <li key={slice.key} onMouseEnter={() => setHovered(slice.key)} onMouseLeave={() => setHovered(null)}>
                  {slice.status ? (
                    <Link
                      className="submission-legend-link"
                      to={accountsLink({ status: slice.status })}
                      title={`Open Accounts filtered by ${slice.label}`}
                    >
                      {content}
                    </Link>
                  ) : (
                    <span className="submission-legend-link">{content}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** Monthly submitted accounts with the year filter, sized like the Accounts status widget. */
export function MonthlySubmittedWidget({ summary, failed, onYear }: WidgetProps & { onYear: (year: number) => void }) {
  const monthly = summary?.monthlySubmitted ?? new Array<number>(12).fill(0);
  const top = axisMax(monthly);
  const currentYear = new Date().getFullYear();
  const availableYears =
    summary?.years && summary.years.length > 1
      ? summary.years
      : Array.from(
          new Set([
            ...(summary?.years ?? [summary?.year ?? currentYear]),
            currentYear,
            currentYear - 1,
            currentYear - 2,
          ]),
        ).sort((a, b) => b - a);

  // A compact plot: 420 x 170 units, drawn to the widget width so the text keeps a readable size.
  const left = 30;
  const right = 414;
  const plotTop = 8;
  const plotHeight = 130;
  const base = plotTop + plotHeight;
  const slot = (right - left) / 12;

  return (
    <div className="dashboard-panel submission-charts submission-monthly-widget">
      <div className="dashboard-panel-header d-flex align-items-center justify-space-between">
        <h2 className="m-0">Monthly submitted accounts</h2>
        <select
          className="submission-year-select"
          aria-label="Year"
          value={summary?.year ?? ''}
          onChange={(event) => onYear(Number(event.target.value))}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div className="submission-charts-body">
        {failed && <div className="text-align-center p-1">The submission summary could not be loaded.</div>}
        <svg viewBox="0 0 420 160" className="submission-bars" role="img" aria-label="Monthly submitted accounts">
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const y = plotTop + (plotHeight / 5) * i;
            return (
              <g key={i}>
                <line x1={left} x2={right} y1={y} y2={y} stroke="#e6e6e6" />
                <text x={left - 4} y={y + 3} textAnchor="end" className="submission-axis">
                  {(top / 5) * (5 - i)}
                </text>
              </g>
            );
          })}
          {monthly.map((value, index) => {
            const height = (value / top) * plotHeight;
            return (
              <g key={MONTHS[index]}>
                <rect
                  data-testid={`bar-${MONTHS[index]}`}
                  x={left + slot * index + slot * 0.2}
                  y={base - height}
                  width={slot * 0.6}
                  height={height}
                  fill="#7b3f98"
                >
                  <title>{`${MONTHS[index]}: ${value}`}</title>
                </rect>
                <text x={left + slot * index + slot / 2} y={base + 14} textAnchor="middle" className="submission-axis">
                  {MONTHS[index]}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
